import { enable_promptbook_templates_flag } from "@/services/posthog/feature_flags";
import { useAuth } from "@/services/supabase/auth/AuthContext/useAuth";
import {
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Icon, SidebarItem, TemplateCard } from "@pufflig/prompt-studio-ui";
import { select_template_event } from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePromptbookTemplates } from "../hooks/usePromptbookTemplates";
import { CreatePromptbookModal } from "./CreatePromptbookModal";

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const TemplateModal = (props: TemplateModalProps) => {
  const { isOpen, onClose, onOpen } = props;

  const { user } = useAuth();
  const analytics = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = searchParams.get("team") || undefined;
  const { data: templateList } = usePromptbookTemplates(teamId);
  const {
    isOpen: isOpenPromptbookModal,
    onOpen: onOpenPromptbookModal,
    onClose: onClosePromptbookModal,
  } = useDisclosure();

  const [selectedFilter, setSelectedFilter] = useState<"all" | "own" | "showcase">("all");
  const [searchName, setSearchName] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const canEdit = true;
  const enablePromptbookTemplates = useFeatureFlagEnabled(enable_promptbook_templates_flag);

  return (
    <>
      <Modal variant="large" size="6xl" isOpen={isOpen} closeOnOverlayClick={false} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader padding="spacing-lg">
            <Text textStyle="heading/2">Templates</Text>
            <ModalCloseButton mt="4px" />
          </ModalHeader>
          <ModalBody display="flex" flexDirection="row">
            <Stack minWidth="12rem">
              <SidebarItem
                label="All templates"
                isSelected={selectedFilter === "all"}
                onClick={() => setSelectedFilter("all")}
              />
              {enablePromptbookTemplates && (
                <>
                  <SidebarItem
                    label="Showcase templates"
                    isSelected={selectedFilter === "showcase"}
                    onClick={() => setSelectedFilter("showcase")}
                  />
                  <SidebarItem
                    label="My templates"
                    isSelected={selectedFilter === "own"}
                    onClick={() => setSelectedFilter("own")}
                  />
                </>
              )}
            </Stack>
            <Flex>
              <Divider orientation="vertical" mx="4" height="100%" />
            </Flex>

            <Flex mb="spacing-xxl" direction="column" width="100%" gap="spacing-lg" minHeight="30rem">
              <InputGroup width="25rem">
                <Input
                  size="lg"
                  py="spacing-lg"
                  placeholder="Search templates..."
                  onChange={(e) => {
                    setSearchName(e.target.value);
                  }}
                />
                <InputLeftElement p="spacing-lg">
                  <Icon name="search-normal-1" />
                </InputLeftElement>
              </InputGroup>

              <Flex width="100%" gap="spacing-lg" flexWrap="wrap" overflowY="auto" maxH="30rem">
                {templateList
                  ?.sort((a, b) => {
                    if (a.name > b.name) return 1;
                    if (a.name < b.name) return -1;
                    return 0;
                  })
                  .filter((a) => a.name.toLowerCase().includes(searchName.toLowerCase()))
                  .filter((template) => {
                    if (selectedFilter === "own") {
                      return template.created_by === user?.id;
                    }
                    if (selectedFilter === "showcase") {
                      return (template as any).isShowcase || false;
                    }
                    return true;
                  })
                  .map((template) => (
                    <TemplateCard
                      isDisabled={!canEdit}
                      key={template.resource_id}
                      title={template.name || ""}
                      description={template.description || ""}
                      picture={template.thumbnail_url || ""}
                      onClick={async () => {
                        analytics.capture(select_template_event, {
                          id: template.resource_id,
                        });
                        setSelectedTemplateId(template.resource_id);
                        searchParams.set("templateId", template.resource_id);
                        setSearchParams(searchParams.toString());
                        onOpenPromptbookModal();
                        onClose();
                      }}
                    />
                  ))}
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <CreatePromptbookModal
        isOpen={isOpenPromptbookModal}
        onClose={() => {
          searchParams.delete("templateId");
          setSearchParams(searchParams.toString());
          onClosePromptbookModal();
        }}
        selectedTemplateId={selectedTemplateId}
        onOpenTemplateModal={onOpen}
      />
    </>
  );
};
